import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import axios from "../axios";
import { useThemePreview } from "../themePreview";
import EntryWizard from "../components/EntryWizard";
import {
  LogoCarousel,
  LunchBanner,
  PhotoCarousel,
  eventHeroSlides,
  scheduleWhen,
  showDate,
  typeName,
  yearSpan,
} from "../components/ShowPieces";

const DONATE_URL = "https://www.paypal.com/donate/?hosted_button_id=RK7LZ4HM2NEBA";

function offerFee(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return "no charge";
  }
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function ShowSide({ event }) {
  const lunch = event.lunches?.[0];
  const tickets = lunch?.ticket_types || [];
  const classes = event.classes || [];

  const scrollToLunch = () => {
    document.getElementById("luncheon")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <aside className="show-side" aria-label="Show details">
      <Link className="show-side-volunteer" to="/company-information?section=volunteers" title="Volunteers Wanted">
        Volunteers Wanted
      </Link>
      <section className="show-side-card">
        <h2>Pricing</h2>
        <dl>
          <div className="show-side-reg">
            <dt>Registered Entry</dt>
            <dd>{offerFee(event.entry_fee)}</dd>
            <p className="show-side-note">* 2 spectator tickets per entry</p>
          </div>
          <div>
            <dt>Guest ticket</dt>
            <dd>{offerFee(event.spectator_fee)}</dd>
          </div>
          {lunch ? (
            <div>
              <dt>VIP luncheon</dt>
              <dd>
                <button
                  type="button"
                  className="show-side-lunch"
                  title="Learn more about the VIP luncheon"
                  aria-label="Learn more about the VIP luncheon"
                  onClick={scrollToLunch}
                >
                  Learn more
                </button>
              </dd>
            </div>
          ) : null}
          {tickets.map((ticket) => (
            <div key={ticket.id} className="show-side-ticket">
              <dt>{ticket.name}</dt>
              <dd>{offerFee(ticket.price)}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="show-side-card">
        <h2>The numbers</h2>
        <dl>
          <div>
            <dt>Entries registered</dt>
            <dd>{event.car_count || 0}</dd>
          </div>
          <div>
            <dt>Classes</dt>
            <dd>{classes.length}</dd>
          </div>
          <div>
            <dt>Trophies</dt>
            <dd>Soon</dd>
          </div>
        </dl>
      </section>
      <a className="show-side-donate" href={DONATE_URL} target="_blank" rel="noreferrer" title="Please DONATE">
        <span>Please DONATE</span>
      </a>
    </aside>
  );
}

function jumpTo(event, id) {
  event.preventDefault();
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(id)?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

function ShowDetailCard({ event }) {
  return (
    <>
      <p className="site-kicker">The show</p>
      <h1>{event.title}</h1>
      <p className="hero-dates">{showDate(event.date)}</p>
      <nav className="hero-jumps" aria-label="On this page">
        <a href="#show-schedule" onClick={(eventClick) => jumpTo(eventClick, "show-schedule")}>
          Schedule
        </a>
        <a href="#show-rules" onClick={(eventClick) => jumpTo(eventClick, "show-rules")}>
          Rules
        </a>
        <a href="#show-photos" onClick={(eventClick) => jumpTo(eventClick, "show-photos")}>
          Photos
        </a>
      </nav>
    </>
  );
}

const PLACE_HEADING = {
  First: "1st place winner",
  Second: "2nd place winner",
  Third: "3rd place winner",
};

function placeHeading(place) {
  return PLACE_HEADING[place] || place;
}

const HERO_DIVISION = { concours: "Concours", street_race: "Street" };
const PLACE_WORD = { 1: "first", 2: "second", 3: "third" };

function classWithoutDivision(title, division) {
  const name = (title || "").trim();
  if (!division) {
    return name;
  }
  const cut = name.replace(new RegExp(`\\s*[-–—]\\s*${division}\\s*$`, "i"), "").trim();
  return cut || name;
}

function awardLine(winner) {
  const division = HERO_DIVISION[winner.division] || "";
  const className = classWithoutDivision(winner.car_class_title, division);
  const place = PLACE_WORD[Number(winner.place)];
  if (division && place) {
    return className ? `${division} ${place} place in ${className}.` : `${division} ${place} place.`;
  }
  const trophy = (winner.trophy || "").trim();
  if (trophy && className) {
    return trophy === "Winner" ? `Winner of ${className}.` : `${trophy} in ${className}.`;
  }
  return [trophy, className].filter(Boolean).join(" ");
}

function WinnerCard({ winner }) {
  const car = winner.vehicle || {};
  const name = [car.year, car.manufacture, car.model].filter(Boolean).join(" ");
  const line = awardLine(winner);
  return (
    <>
      <p className="site-kicker">Congratulations</p>
      {winner.winner_name ? <h2>{winner.winner_name}</h2> : null}
      {line ? <p className="hero-award">{line}</p> : null}
      <a className="hero-winners" href="#show-classes" onClick={(eventClick) => jumpTo(eventClick, "show-classes")}>
        View winners
      </a>
      {name ? (
        <div className="hero-car">
          <p className="site-kicker">The car</p>
          <p className="hero-car-name">{name}</p>
        </div>
      ) : null}
    </>
  );
}

function classBrands(item, makes) {
  const ids = new Set((item.manufacture_ids || []).map(String));
  if (!ids.size) {
    return [];
  }
  return makes.filter((make) => ids.has(String(make.id)));
}

const PHOTO_FILTERS = [
  ["all", "All photos"],
  ["trophy", "Trophy photos"],
  ["winner", "Winner photos"],
  ["car", "Car photos"],
];

function classPhotoList(item) {
  const list = [];
  (item?.results?.rows || []).forEach((row) => {
    (row.winners || []).forEach((winner) => {
      ["trophy", "winner", "car"].forEach((kind) => {
        (winner.photos?.[kind] || []).forEach((photo) => {
          list.push({ ...photo, kind, winnerId: winner.id, car: winner.car });
        });
      });
    });
  });
  return list;
}

function photoWords(photo) {
  if (photo.kind === "trophy") {
    return "Trophy";
  }
  if (photo.kind === "winner") {
    return "Winner on stage";
  }
  return photo.car || "Winning car";
}

function ClassDetail({ item, makes, initialWinnerId = null, focusGallery = false, onClose }) {
  const brands = item ? classBrands(item, makes) : [];
  const results = item?.results;
  const photos = classPhotoList(item);
  const [kind, setKind] = useState("all");
  const [winnerId, setWinnerId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(true);
  const [stagePhoto, setStagePhoto] = useState(null);
  const stageRef = useRef(null);

  useEffect(() => {
    setKind("all");
    setWinnerId(initialWinnerId);
    setOpenId(null);
    setGalleryOpen(true);
    setStagePhoto(null);
  }, [item?.id, initialWinnerId]);

  useEffect(() => {
    if (!item || !focusGallery) {
      return undefined;
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => {
      document.querySelector(".class-window .class-gallery")?.scrollIntoView({
        block: "start",
        behavior: reduce ? "auto" : "smooth",
      });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [item?.id, focusGallery, initialWinnerId]);

  const shown = photos.filter((photo) => {
    if (kind !== "all" && photo.kind !== kind) {
      return false;
    }
    if (winnerId && photo.winnerId !== winnerId) {
      return false;
    }
    return true;
  });
  const openIndex = shown.findIndex((photo) => photo.id === openId);
  const openPhoto = openIndex >= 0 ? shown[openIndex] : null;

  useEffect(() => {
    if (!openId) {
      return;
    }
    setStagePhoto((current) => {
      if (current?.id === openId) {
        return current;
      }
      return shown.find((item) => item.id === openId) || current;
    });
  }, [openId, shown]);

  useEffect(() => {
    if (!openId || stagePhoto?.id !== openId) {
      return undefined;
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => {
      stageRef.current?.scrollIntoView({ block: "end", behavior: reduce ? "auto" : "smooth" });
    }, reduce ? 0 : 340);
    return () => window.clearTimeout(timer);
  }, [openId, stagePhoto]);

  const chosen = (results?.rows || [])
    .flatMap((row) => row.winners || [])
    .find((winner) => winner.id === winnerId);

  const pickWinner = (winner) => {
    if (!winner.car) {
      return;
    }
    setOpenId(null);
    const filtering = winnerId !== winner.id;
    setWinnerId(filtering ? winner.id : null);
    if (filtering) {
      setGalleryOpen(true);
    }
  };

  const stepPhoto = (delta) => {
    if (!shown.length) {
      return;
    }
    const next = (openIndex + delta + shown.length) % shown.length;
    setOpenId(shown[next].id);
  };

  return (
    <Modal
      show={Boolean(item)}
      onHide={onClose}
      centered
      scrollable
      size="xl"
      className="class-detail-modal class-window"
    >
      <Modal.Header closeButton>
        <Modal.Title>{item?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {item ? (
          <>
            <div className="class-detail-top">
              <section className="class-detail-card">
                <h3>Class details</h3>
                <dl className="class-detail-facts">
                  <div>
                    <dt>Type</dt>
                    <dd>{typeName(item.vehicle_type)}</dd>
                  </div>
                  <div>
                    <dt>Years</dt>
                    <dd>{yearSpan(item)}</dd>
                  </div>
                  {item.section ? (
                    <div>
                      <dt>Section</dt>
                      <dd>{item.section}</dd>
                    </div>
                  ) : null}
                </dl>
              </section>
              {results ? (
                <section className="class-detail-card class-detail-winners">
                <h3>Class Trophy Winners</h3>
                <table className="class-detail-awards">
                  <thead>
                    <tr>
                      <th scope="col">{results.heading}</th>
                      {results.places.map((place) => (
                        <th key={place} scope="col">
                          {placeHeading(place)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((row) => (
                      <tr key={row.label || "places"}>
                        {row.label ? <th scope="row">{row.label}</th> : <td />}
                        {(row.winners || []).map((winner, index) => {
                          const place = results.places[index] || "";
                          const filterName = row.label
                            ? `Filter by ${row.label} ${place} Place Winner`
                            : `Filter by ${place}`;
                          return (
                          <td key={winner.id}>
                            {winner.car ? (
                              <button
                                type="button"
                                className="class-winner-pick"
                                aria-pressed={winnerId === winner.id}
                                aria-label={filterName}
                                title={filterName}
                                onClick={() => pickWinner(winner)}
                              >
                                <span className="class-winner-car">{winner.car}</span>
                                {winner.name ? <span className="class-winner-name">{winner.name}</span> : null}
                              </button>
                            ) : null}
                          </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </section>
              ) : null}
            </div>
            <div className="class-detail-makes">
              <p>Makes in this class</p>
            </div>
            {brands.length ? <LogoCarousel makes={brands} quiet /> : null}
            <div className={galleryOpen ? "class-gallery is-open" : "class-gallery"}>
              <h3>
                <button
                  type="button"
                  aria-expanded={galleryOpen}
                  onClick={() => setGalleryOpen((open) => !open)}
                >
                  Class Trophy Gallery
                  <span className="class-gallery-chevron" aria-hidden="true" />
                </button>
              </h3>
              <div className="accordion-fold">
                <div className="accordion-panel" inert={!galleryOpen}>
            <div className="class-photo-block">
              <div className="class-photo-filters" role="group" aria-label="Which photos">
                {PHOTO_FILTERS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className="class-photo-filter"
                    aria-pressed={kind === value}
                    onClick={() => {
                      setKind(value);
                      setOpenId(null);
                    }}
                  >
                    {label}
                  </button>
                ))}
                {chosen ? (
                  <p className="class-photo-who">
                    <span>Filter: {chosen.car}</span>
                    <button
                      type="button"
                      className="class-photo-clear"
                      aria-label={`Remove the ${chosen.car} filter`}
                      title={`Remove the ${chosen.car} filter`}
                      onClick={() => {
                        setWinnerId(null);
                        setOpenId(null);
                      }}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </p>
                ) : null}
              </div>
              {shown.length === 0 ? (
                <p>No photos yet.</p>
              ) : (
                <>
                  <div
                    ref={stageRef}
                    className={openPhoto ? "class-photo-stage-wrap is-open" : "class-photo-stage-wrap"}
                  >
                    <div className="accordion-fold">
                      <div className="accordion-panel" inert={!openPhoto}>
                        {stagePhoto ? (
                          <div className="class-photo-stage">
                            <img key={stagePhoto.id} src={stagePhoto.image} alt={photoWords(stagePhoto)} />
                            <div className="class-photo-stage-actions">
                              <button type="button" onClick={() => stepPhoto(-1)}>
                                Previous
                              </button>
                              <button type="button" onClick={() => setOpenId(null)}>
                                Close
                              </button>
                              <button type="button" onClick={() => stepPhoto(1)}>
                                Next
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className={openPhoto ? "class-photo-grid-wrap" : "class-photo-grid-wrap is-open"}>
                    <div className="accordion-fold">
                      <div className="accordion-panel" inert={Boolean(openPhoto)}>
                        <ul className="class-photo-grid">
                          {shown.map((photo) => {
                            const name = `Show ${photoWords(photo)}`;
                            return (
                              <li key={photo.id}>
                                <button type="button" aria-label={name} title={name} onClick={() => setOpenId(photo.id)}>
                                  <img src={photo.thumb || photo.image} alt="" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}

function winnerDetail(item, row, winner, placeIndex) {
  const place = (item.results?.places || [])[placeIndex] || "";
  return {
    classTitle: item.title || "",
    division: row.label || "",
    place,
    car: winner.car || "",
    name: winner.name || "",
  };
}

function galleryPhotos(event) {
  const lawn = (event.photos || [])
    .filter((photo) => photo.on_gallery && photo.image)
    .map((photo) => ({
      id: `lawn-${photo.id}`,
      image: photo.image,
      thumb: photo.image,
      photographer: photo.photographer || "",
      winner: null,
    }));
  const awards = [];
  (event.classes || []).forEach((item) => {
    (item.results?.rows || []).forEach((row) => {
      (row.winners || []).forEach((winner, placeIndex) => {
        const detail = winner.car ? winnerDetail(item, row, winner, placeIndex) : null;
        ["trophy", "winner"].forEach((kind) => {
          (winner.photos?.[kind] || []).forEach((photo) => {
            const image = photo.image || photo.thumb;
            if (!image) {
              return;
            }
            awards.push({
              id: photo.id,
              image,
              thumb: photo.thumb || image,
              photographer: "",
              winner: detail,
            });
          });
        });
      });
    });
  });
  return [...lawn, ...awards];
}

function photoName(photo, index) {
  if (photo.winner) {
    const place = [photo.winner.division, photo.winner.place].filter(Boolean).join(" ");
    return [place, photo.winner.classTitle, photo.winner.car, photo.winner.name].filter(Boolean).join(", ");
  }
  return photo.photographer ? `Photo by ${photo.photographer}` : `Photo ${index + 1}`;
}

function ShowGallery({ photos }) {
  const [index, setIndex] = useState(null);
  const [perRow, setPerRow] = useState(6);
  const count = photos.length;
  const open = index !== null && index < count;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setIndex((current) => (current + 1) % count);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setIndex((current) => (current - 1 + count) % count);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, count]);

  const step = (delta) => setIndex((current) => (current + delta + count) % count);
  const photo = open ? photos[index] : null;

  return (
    <section className="site-section">
      <div className="container">
        <article id="show-photos" className="company-card company-card-wide show-gallery">
          <header className="company-card-head">
            <div>
              <p className="site-kicker">On the lawn</p>
              <h2>Photo gallery</h2>
            </div>
            {count > 0 ? (
              <div className="show-gallery-layout" role="group" aria-label="Photos per row">
                {[3, 6].map((columns) => (
                  <button
                    key={columns}
                    type="button"
                    className={columns === perRow ? "is-on" : undefined}
                    aria-pressed={columns === perRow}
                    onClick={() => setPerRow(columns)}
                  >
                    {columns} per row
                  </button>
                ))}
              </div>
            ) : null}
          </header>
          <div className="company-card-body">
            {count === 0 ? (
              <p>Photos from this show will be posted here.</p>
            ) : (
              <ul className={perRow === 3 ? "show-gallery-grid is-three" : "show-gallery-grid is-six"}>
                {photos.map((item, itemIndex) => {
                  const name = photoName(item, itemIndex);
                  return (
                    <li key={item.id}>
                      <button type="button" aria-label={name} title={name} onClick={() => setIndex(itemIndex)}>
                        <img src={item.thumb || item.image} alt="" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </article>
      </div>
      <Modal
        show={open}
        onHide={() => setIndex(null)}
        centered
        className="class-detail-modal show-gallery-modal"
        aria-labelledby="show-gallery-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="show-gallery-title">{photo ? photoName(photo, index) : "Photo gallery"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {photo ? (
            <>
              <div className="show-gallery-stage">
                <img src={photo.image} alt="" />
                {count > 1 ? (
                  <>
                    <button
                      type="button"
                      className="show-gallery-nav show-gallery-prev"
                      aria-label="Previous photo"
                      title="Previous photo"
                      onClick={() => step(-1)}
                    >
                      <span aria-hidden="true">‹</span>
                    </button>
                    <button
                      type="button"
                      className="show-gallery-nav show-gallery-next"
                      aria-label="Next photo"
                      title="Next photo"
                      onClick={() => step(1)}
                    >
                      <span aria-hidden="true">›</span>
                    </button>
                  </>
                ) : null}
              </div>
              {photo.winner ? (
                <div className="show-gallery-winner">
                  <p className="show-gallery-place">
                    {[photo.winner.division, photo.winner.place].filter(Boolean).join(" ")}
                  </p>
                  {photo.winner.classTitle ? <p className="show-gallery-class">{photo.winner.classTitle}</p> : null}
                  <p>{[photo.winner.car, photo.winner.name].filter(Boolean).join(" · ")}</p>
                </div>
              ) : null}
              <p className="show-gallery-count">
                {index + 1} of {count}
              </p>
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </section>
  );
}

function clubHref(website) {
  const value = (website || "").trim();
  if (!value) {
    return "";
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  return `https://${value}`;
}

function ShowClubs({ clubs }) {
  if (!clubs.length) {
    return null;
  }
  return (
    <div className="container show-clubs-slot">
      <article className="company-card company-card-wide show-clubs">
        <header className="company-card-head">
          <p className="site-kicker">At this show</p>
          <h2>Clubs</h2>
        </header>
        <div className="company-card-body">
          <ul className="show-club-grid">
            {clubs.map((club) => {
              const href = clubHref(club.website);
              const name = href ? (
                <a className="show-club-name" href={href} target="_blank" rel="noreferrer">
                  {club.title}
                </a>
              ) : (
                <strong className="show-club-name">{club.title}</strong>
              );
              return (
                <li key={club.id} className="show-club">
                  {club.logo_url ? <img src={club.logo_url} alt="" /> : null}
                  <div>
                    {name}
                    {club.location ? (
                      <p className="show-club-place">
                        <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                        <span>{club.location}</span>
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </article>
    </div>
  );
}

function EventScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const { images } = useThemePreview();
  const [event, setEvent] = useState(null);
  const [missing, setMissing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [startStage, setStartStage] = useState(1);
  const [purpose, setPurpose] = useState("register");
  const [signInFor, setSignInFor] = useState("register");
  const [makes, setMakes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [openClass, setOpenClass] = useState(null);
  const [classWinnerId, setClassWinnerId] = useState(null);
  const [classGalleryFocus, setClassGalleryFocus] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const signInRef = useRef(null);

  useEffect(() => {
    axios
      .get("/api/users/events/")
      .then(({ data }) => {
        const match = data.find((item) => String(item.id) === String(id));
        setEvent(match || null);
        setMissing(!match);
      })
      .catch(() => setMissing(true));
    axios
      .get("/api/users/manufactures/")
      .then(({ data }) => setMakes(data))
      .catch(() => setMakes([]));
    axios
      .get("/api/users/results/")
      .then(({ data }) => setWinners(data))
      .catch(() => setWinners([]));
  }, [id]);

  useEffect(() => {
    if (!event) {
      return;
    }
    const classId = searchParams.get("class");
    if (!classId) {
      return;
    }
    const match = (event.classes || []).find((item) => String(item.id) === String(classId));
    if (!match) {
      return;
    }
    const winner = Number(searchParams.get("winner"));
    setClassWinnerId(Number.isFinite(winner) ? winner : null);
    setClassGalleryFocus(true);
    setOpenClass(match);
  }, [event, searchParams]);

  const slides = eventHeroSlides(event, winners);
  const heroSlides = slides.length ? slides : [{ src: images.hero, kind: "show" }];
  const lunch = event?.lunches?.[0];
  const classes = event?.classes || [];
  const openWizard = (stage, nextPurpose = "register") => {
    if (!userInfo) {
      setSignInFor(nextPurpose);
      setSignInOpen(true);
      return;
    }
    setPurpose(nextPurpose);
    setStartStage(stage);
    setOpen(true);
  };

  const goSignIn = () => {
    setSignInOpen(false);
    const query =
      signInFor === "vip" ? "vip=1" : signInFor === "spectator" ? "spectator=1" : "register=1";
    navigate("/login", { state: { from: `/show/${id}?${query}` } });
  };

  useEffect(() => {
    if (!event) {
      return;
    }
    const register = searchParams.get("register") === "1";
    const spectator = searchParams.get("spectator") === "1";
    const vip = searchParams.get("vip") === "1" || searchParams.get("tickets") === "1";
    const checkout = searchParams.get("checkout") === "1";
    if (!register && !spectator && !vip && !checkout) {
      return;
    }
    if (!userInfo) {
      if ((register || spectator || vip) && !checkout) {
        setSignInFor(vip ? "vip" : spectator ? "spectator" : "register");
        setSignInOpen(true);
      } else {
        navigate("/login", { state: { from: `/show/${id}?${searchParams.toString()}` } });
        return;
      }
    } else {
      setPurpose(vip ? "vip" : spectator ? "spectator" : "register");
      setStartStage(checkout ? 5 : vip || spectator ? 3 : 1);
      setOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("register");
    next.delete("spectator");
    next.delete("vip");
    next.delete("tickets");
    next.delete("checkout");
    setSearchParams(next, { replace: true });
  }, [event, userInfo, id, navigate, searchParams, setSearchParams]);

  const scrollToLunch = () => {
    document.getElementById("luncheon")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (missing) {
    return (
      <div className="beach">
        <section className="site-section">
          <div className="site-wrap">
            <h1>That show is not on the list.</h1>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="beach">
      <PhotoCarousel
        slides={heroSlides}
        renderCard={
          event
            ? (slide) =>
                slide?.kind === "winner" ? (
                  <WinnerCard winner={slide.winner} />
                ) : (
                  <ShowDetailCard event={event} />
                )
            : null
        }
      />
      <div className="drive-band">
        <button type="button" className="btn btn-site" onClick={() => openWizard(1)}>
          Register a car
        </button>
        <button type="button" className="btn btn-site" onClick={() => openWizard(3, "spectator")}>
          Spectator tickets
        </button>
        {lunch ? (
          <button type="button" className="btn btn-site-quiet" onClick={scrollToLunch}>
            VIP Luncheon
          </button>
        ) : null}
      </div>

      {event && (
        <>
          <section className="site-section show-lead">
            <div className="container show-intro">
              <div>
                <p className="site-kicker">The show</p>
                <h1>{event.title}</h1>
                <p className="hero-dates">{showDate(event.date)}</p>
                {event.description ? (
                  <div className="event-copy" dangerouslySetInnerHTML={{ __html: event.description }} />
                ) : null}
                <div className="show-day">
                  <article id="show-schedule" className="company-card company-card-wide">
                    <header className="company-card-head">
                      <p className="site-kicker">The day</p>
                      <h2 id="show-schedule-title">Schedule</h2>
                    </header>
                    <div className="company-card-body">
                      {(event.schedule || []).length === 0 ? (
                        <p>The schedule for this day will be posted here.</p>
                      ) : (
                        <div className="show-schedule-wrap">
                          <table className="show-schedule">
                            <thead>
                              <tr>
                                <th scope="col">Time</th>
                                <th scope="col">What happens</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...event.schedule]
                                .sort((a, b) => a.position - b.position || a.id - b.id)
                                .map((line) => (
                                  <tr key={line.id}>
                                    <td>{scheduleWhen(line)}</td>
                                    <td>{line.label}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              </div>
              <ShowSide event={event} />
            </div>
            <div className="container show-classes-slot">
              <article id="show-classes" className="company-card company-card-wide show-classes">
                <header className="company-card-head">
                  <p className="site-kicker">The field</p>
                  <h2>Classes</h2>
                </header>
                <div className="company-card-body">
                  {classes.length === 0 ? (
                    <p>Classes for this event will be listed here.</p>
                  ) : (
                    <div className="class-sheet-wrap">
                      <table className="class-sheet">
                        <tbody>
                          {classes.map((item) => {
                            const results = item.results || { heading: "", places: [], rows: [] };
                            const span = 1 + results.rows.length;
                            const brandNames = classBrands(item, makes).map((make) => make.title);
                            const makesLabel = brandNames.length ? brandNames.join(", ") : "Any";
                            return (
                              <Fragment key={item.id}>
                                <tr className="class-award-head">
                                  <th className="class-award-name" scope="row" rowSpan={span}>
                                    <button
                                      type="button"
                                      className="class-open"
                                      onClick={() => setOpenClass(item)}
                                    >
                                      {item.title}
                                    </button>
                                    <dl className="class-award-facts">
                                      <div>
                                        <dt>Type</dt>
                                        <dd>{typeName(item.vehicle_type)}</dd>
                                      </div>
                                      <div>
                                        <dt>Years</dt>
                                        <dd>{yearSpan(item)}</dd>
                                      </div>
                                      {item.section ? (
                                        <div>
                                          <dt>Section</dt>
                                          <dd>{item.section}</dd>
                                        </div>
                                      ) : null}
                                      <div>
                                        <dt>Makes</dt>
                                        <dd className="class-award-makes" title={makesLabel}>
                                          {makesLabel}
                                        </dd>
                                      </div>
                                    </dl>
                                    <button
                                      type="button"
                                      className="class-gallery-open"
                                      onClick={() => {
                                        setClassWinnerId(null);
                                        setClassGalleryFocus(true);
                                        setOpenClass(item);
                                      }}
                                    >
                                      View Photo Gallery
                                    </button>
                                  </th>
                                  <th scope="col">{results.heading}</th>
                                  {results.places.map((place) => (
                                    <th key={place} scope="col">
                                      {placeHeading(place)}
                                    </th>
                                  ))}
                                </tr>
                                {results.rows.map((row) => (
                                  <tr key={row.label || "places"} className="class-award-row">
                                    {row.label ? <th scope="row">{row.label}</th> : <td />}
                                    {(row.winners || []).map((winner, index) => {
                                      const place = results.places[index] || "";
                                      const photoName = row.label
                                        ? `View ${row.label} ${place} place photos for ${winner.car}`
                                        : `View ${place} photos for ${winner.car}`;
                                      return (
                                        <td key={winner.id}>
                                          {winner.car ? (
                                            <button
                                              type="button"
                                              className="class-sheet-winner"
                                              aria-label={photoName}
                                              title={photoName}
                                              onClick={() => {
                                                setClassWinnerId(winner.id);
                                                setClassGalleryFocus(true);
                                                setOpenClass(item);
                                              }}
                                            >
                                              <span className="class-winner-car">{winner.car}</span>
                                              {winner.name ? <span className="class-winner-name">{winner.name}</span> : null}
                                            </button>
                                          ) : null}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </article>
            </div>
            <div className="container show-rules">
              <article id="show-rules" className="company-card company-card-wide">
                <header className="company-card-head">
                  <div>
                    <p className="site-kicker">The entries</p>
                    <h2 id="show-rules-title">Rules for the entries</h2>
                  </div>
                  <Link className="show-rules-link" to="/awards?section=judging">
                    View the Judging rules
                  </Link>
                </header>
                <div className="company-card-body">
                  {(event.rules || []).length === 0 ? (
                    <p>The rules for this day will be posted here.</p>
                  ) : (
                    <ol className="judge-rules entry-rules">
                      {[...event.rules]
                        .sort((a, b) => a.position - b.position || a.id - b.id)
                        .map((rule) => (
                          <li key={rule.id}>
                            <span>{rule.text}</span>
                          </li>
                        ))}
                    </ol>
                  )}
                </div>
              </article>
            </div>
            {(event.clubs || []).length > 0 ? <ShowClubs clubs={event.clubs} /> : null}
          </section>

          {makes.length > 0 && (
            <section className="site-section site-section-tight">
              <LogoCarousel makes={makes} />
            </section>
          )}

          <ShowGallery photos={galleryPhotos(event)} />

          {lunch ? (
            <section className="site-section site-section-sand" id="luncheon">
              <div className="container">
                <LunchBanner lunch={lunch} onGetTickets={() => openWizard(3, "vip")} />
              </div>
            </section>
          ) : null}

          <ClassDetail
            item={openClass}
            makes={makes}
            initialWinnerId={classWinnerId}
            focusGallery={classGalleryFocus}
            onClose={() => {
              setOpenClass(null);
              setClassWinnerId(null);
              setClassGalleryFocus(false);
              setSearchParams((current) => {
                const next = new URLSearchParams(current);
                next.delete("class");
                next.delete("winner");
                return next;
              }, { replace: true });
            }}
          />
          <EntryWizard
            event={event}
            userInfo={userInfo}
            open={open}
            startStage={startStage}
            purpose={purpose}
            onClose={() => setOpen(false)}
          />
        </>
      )}
      <Modal
        show={signInOpen}
        onHide={() => setSignInOpen(false)}
        centered
        className="confirm-dialog signin-prompt"
        aria-labelledby="signin-prompt-title"
        aria-describedby="signin-prompt-body"
        onEntered={() => signInRef.current?.focus()}
      >
        <Modal.Header closeButton>
          <Modal.Title id="signin-prompt-title">Sign in</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p id="signin-prompt-body">
            {signInFor === "vip"
              ? "You must sign in to buy a VIP luncheon ticket."
              : signInFor === "spectator"
                ? "You must sign in to buy spectator tickets."
                : "You must sign in to register a car."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="signin-go" ref={signInRef} onClick={goSignIn}>
            Sign in / Sign up
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EventScreen;
